import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { StaffBookingSummaryResponse } from "@repo/api-client";
import { PageScaffold } from "../shell/PageScaffold";
import { useVenue } from "../shell/VenueContext";
import { canAccessVenue } from "../shell/roles";
import { BookingPeekPanel } from "./BookingPeekPanel";
import { CancelDialog } from "./CancelDialog";
import { EditBookingDialog } from "./EditBookingDialog";
import { FilterBar } from "./FilterBar";
import { ManualEntryDialog, type ManualEntryPrefill } from "./ManualEntryDialog";
import { NoShowChargeDialog } from "../features/noShowCharge/NoShowChargeDialog";
import { ViewSwitcher } from "./ViewSwitcher";
import { todayServiceDay } from "./format";
import {
  EMPTY_FILTER,
  parseFilter,
  parseView,
  toSearch,
  type BookingFilter,
  type BookingView,
} from "./searchParams";
import { cancelNeedsStepUp } from "./status";
import { dateTimeInput, primaryButton } from "./styles";
import { CalendarView } from "./views/CalendarView";
import { FloorView } from "./views/FloorView";
import { InboxView } from "./views/InboxView";
import { ListView } from "./views/ListView";
import { TimelineView } from "./views/TimelineView";

type Dialog =
  | { kind: "entry"; prefill?: ManualEntryPrefill }
  | { kind: "edit"; booking: StaffBookingSummaryResponse }
  | { kind: "cancel"; booking: StaffBookingSummaryResponse }
  | { kind: "noShowFee"; booking: StaffBookingSummaryResponse }
  | null;

/**
 * `/v/:venueId/bookings` — the staff back-office booking book. One shared query layer feeds four live
 * views (list · calendar · timeline · floor) plus the read-only request inbox, kept live by the
 * shell's SSE invalidation (`booking.*` → `["venue", venueId, "bookings"]`) and the header's "not
 * live" indicator. View + filters live in the URL; manual entry, edit/move and cancel (with the
 * manager step-up) mount here; a 409 on any mutation opens the canonical conflict modal.
 */
export function BookingsPage() {
  const { venueId, venue } = useVenue();
  const { t } = useTranslation();
  const { claims } = useAuth();
  const [sp, setSp] = useSearchParams();

  const view = parseView(sp);
  const filter = parseFilter(sp);
  const day = sp.get("day") ?? todayServiceDay(venue.timeZone);

  // Landing on a pristine `/bookings` defaults the list to today onward instead of the bare
  // "add a filter to search" prompt — the day-to-day case the staff open the screen for. Runs once;
  // any later explicit filter change (incl. clearing to nothing) is respected.
  const seeded = useRef(false);
  useEffect(() => {
    // Once, on first mount only — later filter edits (incl. clearing) are the user's to keep.
    if (seeded.current) return;
    seeded.current = true;
    if (sp.toString() === "") {
      setSp(toSearch("list", { ...EMPTY_FILTER, from: todayServiceDay(venue.timeZone) }), {
        replace: true,
      });
    }
  }, [sp, setSp, venue.timeZone]);

  const [selected, setSelected] = useState<StaffBookingSummaryResponse | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);

  const canManage = canAccessVenue(claims, venueId, "MANAGER");

  function apply(next: { view?: BookingView; filter?: BookingFilter; day?: string }) {
    setSp(
      toSearch(next.view ?? view, next.filter ?? filter, next.day ?? sp.get("day") ?? undefined),
      { replace: true },
    );
  }

  const closeAll = () => {
    setDialog(null);
    setSelected(null);
  };

  const dayControls = view === "timeline" || view === "floor" || view === "calendar";

  return (
    <PageScaffold
      title={`${t("bookings.title")} — ${venue.displayName}`}
      actions={
        <button
          type="button"
          className={primaryButton}
          onClick={() => setDialog({ kind: "entry" })}
        >
          {t("bookings.action.new")}
        </button>
      }
    >
      <div className={css({ display: "flex", flexDirection: "column", gap: "4" })}>
        <ViewSwitcher view={view} onChange={(v) => apply({ view: v })} />

        {dayControls ? (
          <label
            className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm" })}
          >
            {t("bookings.field.day")}
            <input
              type="date"
              value={day}
              onChange={(e) => apply({ day: e.target.value || undefined })}
              className={dateTimeInput}
            />
          </label>
        ) : null}

        {view !== "inbox" ? (
          <FilterBar filter={filter} onChange={(f) => apply({ filter: f })} />
        ) : null}

        <div
          className={css({
            display: "grid",
            gap: "4",
            gridTemplateColumns: selected ? { base: "1fr", lg: "2fr 1fr" } : "1fr",
            alignItems: "start",
          })}
        >
          <div className={css({ minW: "0" })}>
            {view === "list" ? (
              <ListView
                filter={filter}
                onSelect={setSelected}
                onCreate={() => setDialog({ kind: "entry" })}
              />
            ) : null}
            {view === "calendar" ? (
              <CalendarView
                anchor={day}
                filter={filter}
                onPickDay={(d) => apply({ view: "timeline", day: d })}
                onShiftMonth={(a) => apply({ day: a })}
              />
            ) : null}
            {view === "timeline" ? (
              <TimelineView day={day} filter={filter} onSelect={setSelected} />
            ) : null}
            {view === "floor" ? (
              <FloorView day={day} filter={filter} onSelect={setSelected} />
            ) : null}
            {view === "inbox" ? <InboxView onSelect={setSelected} /> : null}
          </div>

          {selected ? (
            <BookingPeekPanel
              booking={selected}
              onEdit={() => setDialog({ kind: "edit", booking: selected })}
              onCancel={() => setDialog({ kind: "cancel", booking: selected })}
              onRecreate={() =>
                setDialog({
                  kind: "entry",
                  prefill: {
                    serviceId: selected.serviceId,
                    startSec: selected.startSec,
                    partySize: selected.partySize,
                    guestGivenName: selected.guestGivenName,
                    guestFamilyName: selected.guestFamilyName,
                    guestPhone: selected.guestPhone,
                    guestEmail: selected.guestEmail,
                    zonePreference: selected.zonePreference,
                    specialRequests: selected.specialRequests,
                    allergyNote: selected.allergyNote,
                  },
                })
              }
              onNoShowFee={() => setDialog({ kind: "noShowFee", booking: selected })}
              onClose={() => setSelected(null)}
            />
          ) : null}
        </div>
      </div>

      {dialog?.kind === "entry" ? (
        <ManualEntryDialog
          prefill={dialog.prefill}
          canOverride={canManage}
          onClose={() => setDialog(null)}
          onCreated={closeAll}
        />
      ) : null}
      {dialog?.kind === "edit" ? (
        <EditBookingDialog
          booking={dialog.booking}
          canOverride={canManage}
          onClose={() => setDialog(null)}
          onSaved={closeAll}
        />
      ) : null}
      {dialog?.kind === "cancel" ? (
        <CancelDialog
          booking={dialog.booking}
          needsStepUp={cancelNeedsStepUp(canManage)}
          onClose={() => setDialog(null)}
          onCancelled={closeAll}
        />
      ) : null}
      {dialog?.kind === "noShowFee" ? (
        <NoShowChargeDialog
          booking={dialog.booking}
          onClose={() => setDialog(null)}
          onDone={() => {
            /* the charge/refund mutation invalidates the bookings + guarantees families */
          }}
        />
      ) : null}
    </PageScaffold>
  );
}
